import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button } from '../../../../components/ui/button';
import { CenteredSpinner } from '../../../../components/ui/Spinner';
import { loadPostsView, rememberPostsView, type PostsView } from '../../../../lib/socialCalendar';
import { ClubCalendar } from './ClubCalendar';
import { PostRow } from './PostRow';
import { socialApi } from './socialApi';
import { SocialFrame } from './SocialFrame';

// /portal/social/posts — the club's posts, as a List or a Calendar. Each row states,
// in words, whether the public can see it (see socialPostView.ts).
// The Calendar shows the club's Kyck posts from this same query, plus natively published
// posts fetched by the calendar itself (read-only). No drag-to-reschedule, by decision.
// A List / Calendar toggle on this page rather than a new nav row: the nav is settled.

export { PostRow };

const VIEW_LABELS: Record<PostsView, string> = { list: 'List', calendar: 'Calendar' };

function PostsList({ accountId }: { accountId: string }) {
  const q = useQuery({ queryKey: ['social-posts', accountId], queryFn: () => socialApi.posts(accountId), retry: false });
  const [view, setView] = useState<PostsView>(() => loadPostsView());
  const choose = (v: PostsView) => {
    setView(v);
    rememberPostsView(v);
  };
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-foreground">Your posts</h2>
        <div className="flex flex-wrap items-center gap-3">
          <div role="group" aria-label="View" className="flex gap-1">
            {(['list', 'calendar'] as const).map((v) => (
              <Button key={v} type="button" size="sm" variant={view === v ? 'default' : 'outline'} aria-pressed={view === v} onClick={() => choose(v)}>
                {VIEW_LABELS[v]}
              </Button>
            ))}
          </div>
          <Button asChild>
            <Link to={`/portal/social/posts/new?account=${encodeURIComponent(accountId)}`}>New post</Link>
          </Button>
        </div>
      </div>
      {q.isPending ? (
        <CenteredSpinner />
      ) : q.isError ? (
        <p className="text-sm text-destructive" role="alert">
          Could not load your posts. Try again.
        </p>
      ) : view === 'calendar' ? (
        <ClubCalendar posts={q.data.posts} accountId={accountId} />
      ) : q.data.posts.length === 0 ? (
        <p className="text-sm text-muted-foreground">No posts yet. Create your first one.</p>
      ) : (
        <ul className="space-y-3">
          {q.data.posts.map((p) => (
            <PostRow key={p.id} post={p} accountParam={accountId} />
          ))}
        </ul>
      )}
    </div>
  );
}

export function PostsPage() {
  return <SocialFrame tab="posts">{(accountId) => <PostsList key={accountId} accountId={accountId} />}</SocialFrame>;
}
